import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddPlus2, ExitDeleteRemoveClose } from '../../assets/icons';
import { useCreateSupportNoteMutation } from '../../services/supportnote.api';
import { SupportNoteTag } from '../../stores/types/supportnote.interface';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Dropdown,
  DropdownType,
  FormInput,
  Icon,
  Label,
  LabelType,
  useGlobalModalContext,
} from '../_ui';
import { Form } from '../_ui/Form.component';
import { ValidationType } from '../_ui/Input.component';
import InputArea from '../_ui/InputArea.component';
import { AddNoteEditorProps } from './ChargerHistory.interface';

const NOTE_TAG_OPTIONS = [
  { id: SupportNoteTag.CHARGER, label: 'charger_history_tag_charger' },
  { id: SupportNoteTag.LOCATION, label: 'charger_history_tag_location' },
  { id: SupportNoteTag.MODEL, label: 'charger_history_tag_model' },
];

const AddNoteEditor: FC<AddNoteEditorProps> = ({ chargerId }) => {
  const { t } = useTranslation();
  const { hideModal } = useGlobalModalContext();

  const [tag, setTag] = useState<SupportNoteTag>(SupportNoteTag.CHARGER);
  const [title, setTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [links, setLinks] = useState<string[]>(['']);

  const [triggerCreate, createResult] = useCreateSupportNoteMutation();

  const isSaveDisabled = title.trim().length === 0 || noteBody.trim().length === 0;

  const handleAddLink = () => {
    setLinks((prev) => [...prev, '']);
  };

  const handleRemoveLink = (index: number) => {
    setLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLinkChange = (index: number, value: string) => {
    setLinks((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleTagSelect = useCallback((item: { id: SupportNoteTag }) => {
    setTag(item.id);
  }, []);

  const handleSave = () => {
    const cleanedLinks = links.filter((link) => link.trim().length > 0);
    triggerCreate({
      deviceId: chargerId,
      title: title.trim(),
      note: noteBody.trim(),
      tag,
      links: cleanedLinks,
    });
  };

  const tagDropdownItems = useMemo(() => {
    return NOTE_TAG_OPTIONS.map((opt) => ({
      ...opt,
      label: t(opt.label),
      selected: opt.id === tag,
    }));
  }, [tag, t]);

  return (
    <Form
      onSubmit={handleSave}
      queryResult={createResult}
      onQuerySuccess={hideModal}
      onQueryFailed={hideModal}
    >
      <div className='flex flex-col gap-4 w-[500px] max-h-[70vh] overflow-y-auto'>
        {/* Note tag */}
        <div className='flex flex-col gap-2'>
          <Label
            text={t('charger_history_note_tag')}
            type={LabelType.LABEL_M}
            color={ColorType.BLACK}
          />
          <Dropdown
            type={DropdownType.SELECT}
            items={tagDropdownItems}
            headerWidth='100%'
            onItemClick={handleTagSelect}
            placeholder={t('charger_history_tag_charger')}
            labelType={LabelType.BODY3}
            labelTypeHighlighted={LabelType.BODY3}
            chevdownIconHighlightColor={ColorType.GREY6}
            headerHighLightClassName='bg-grey1 border-grey3 rounded'
          />
        </div>

        {/* Title */}
        <div className='flex flex-col gap-2'>
          <Label
            text={t('charger_history_title')}
            type={LabelType.LABEL_M}
            color={ColorType.BLACK}
          />
          <FormInput
            placeholder={t('charger_history_title_placeholder')}
            defaultValue={title}
            width='100%'
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>

        {/* Notes body */}
        <div className='flex flex-col gap-2'>
          <Label
            text={t('charger_history_notes')}
            type={LabelType.LABEL_M}
            color={ColorType.BLACK}
          />
          <InputArea
            height='180px'
            placeholder={t('charger_history_notes_placeholder')}
            defaultValue={noteBody}
            onChange={(newValue) => setNoteBody(newValue)}
            className='placeholder:text-grey4 text-sm font-inter'
          />
        </div>

        {/* Links (optional) */}
        <div className='flex flex-col gap-2'>
          <div className='flex flex-row items-baseline gap-1'>
            <Label
              text={t('charger_history_links')}
              type={LabelType.LABEL_M}
              color={ColorType.BLACK}
            />
            <Label
              text={t('charger_history_optional')}
              type={LabelType.LABEL_M}
              color={ColorType.GREY6}
            />
          </div>
          {links.map((link, index) => (
            <div key={`link-${index}`} className='flex items-center gap-2'>
              <FormInput
                validationType={link.trim().length > 0 ? ValidationType.LINK : ValidationType.NONE}
                placeholder={t('charger_history_link_placeholder')}
                defaultValue={link}
                width='100%'
                onChange={(event) => handleLinkChange(index, event.target.value)}
              />
              {links.length > 1 && (
                <Icon
                  src={ExitDeleteRemoveClose}
                  className='cursor-pointer flex-shrink-0'
                  onClick={() => handleRemoveLink(index)}
                />
              )}
            </div>
          ))}
          <div className='flex items-center gap-1 cursor-pointer w-fit' onClick={handleAddLink}>
            <Icon src={AddPlus2} color={ColorType.BRAND3} />
            <Label
              text={t('charger_history_add_link')}
              type={LabelType.LABEL_M}
              color={ColorType.BRAND3}
              className='cursor-pointer'
            />
          </div>
        </div>

        {/* Save button */}
        <div className='self-stretch justify-center items-center gap-2.5 inline-flex pt-2'>
          <Button
            size={ButtonSize.MEDIUM_FULL}
            label={t('save')}
            type={ButtonType.PRIMARY}
            disabled={isSaveDisabled}
            isSumbit
          />
        </div>
      </div>
    </Form>
  );
};

export default AddNoteEditor;
